/**
 * THE canonical status → colour map.
 *
 * Table statuses, order statuses, payment statuses, production/KDS statuses,
 * task statuses, translation statuses, device statuses and alert severities ALL
 * resolve through this one table. No screen picks its own green.
 *
 * Why this matters operationally: a server glancing at a floor map, a chef
 * glancing at a KDS and a manager glancing at a dashboard must read "late" as
 * the same colour. If each screen invented its own palette, red would mean
 * "urgent" in one place and "cancelled" in another, and staff would misread it
 * during a rush — which is when it costs money.
 *
 * Adding a status: add ONE row here. Never add a colour class to a screen.
 */

/** The seven semantic tones defined in `tokens.css` / `tailwind-preset.cjs`. */
export const TONES = ['neutral', 'info', 'success', 'warning', 'danger', 'busy', 'accent'] as const;
export type Tone = (typeof TONES)[number];

/**
 * Status code -> tone.
 *
 * Keys are the RAW status strings the API emits (SCREAMING_SNAKE), so a lookup
 * is a direct hit with no normalisation guesswork.
 *
 * The tone vocabulary, in words:
 *   neutral — nothing is happening / idle / archived
 *   info    — accepted, queued, moving forward normally
 *   busy    — actively being worked on right now
 *   success — done, paid, published, healthy
 *   warning — needs a human soon: late, low, awaiting review
 *   danger  — failed, cancelled, revoked, overloaded, critical
 *   accent  — reserved / promotional / special handling
 */
export const STATUS_TONE: Record<string, Tone> = {
  // ---- table (organization.ts `tables.status`) -------------------------
  EMPTY: 'neutral',
  OCCUPIED: 'busy',
  RESERVED: 'accent',
  NEEDS_CLEANING: 'warning',
  SETTING_UP: 'info',
  LOCKED: 'danger',
  OUT_OF_SERVICE: 'danger',

  // ---- dining session ---------------------------------------------------
  OPEN: 'busy',
  BILL_REQUESTED: 'warning',
  PAYING: 'warning',
  CLOSED: 'neutral',

  // ---- order (orders.ts `orders.status`) -------------------------------
  DRAFT: 'neutral',
  PENDING_CONFIRM: 'warning',
  CONFIRMED: 'info',
  SENT_TO_STATION: 'info',
  IN_PRODUCTION: 'busy',
  PARTIALLY_READY: 'busy',
  READY: 'success',
  SERVED: 'success',
  COMPLETED: 'success',
  CANCELLED: 'danger',
  REJECTED: 'danger',

  // ---- payment ----------------------------------------------------------
  UNPAID: 'warning',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  OVERPAID: 'warning',
  REFUNDED: 'neutral',
  PARTIALLY_REFUNDED: 'warning',
  VOIDED: 'danger',
  CAPTURED: 'success',
  AUTHORIZED: 'info',
  FAILED: 'danger',

  // ---- production / KDS (production.ts ticket + item statuses) ----------
  WAITING: 'warning',
  OFFERED: 'warning',
  ACCEPTED: 'info',
  PREPARING: 'busy',
  PAUSED: 'warning',
  WAITING_INGREDIENT: 'danger',
  REMAKE_REQUIRED: 'danger',
  REMAKING: 'busy',
  WAITING_PICKUP: 'success',
  PICKED_UP: 'info',
  DELIVERED: 'success',
  RETURNED: 'danger',

  // ---- task / dispatch (staff.ts) --------------------------------------
  ASSIGNED: 'info',
  IN_PROGRESS: 'busy',
  DONE: 'success',
  EXPIRED: 'danger',
  REASSIGNED: 'warning',
  ESCALATED: 'danger',

  // ---- employee presence -----------------------------------------------
  OFFLINE: 'neutral',
  NOT_ON_SHIFT: 'neutral',
  AVAILABLE: 'success',
  BUSY: 'busy',
  ON_BREAK: 'warning',

  // ---- generic lifecycle -----------------------------------------------
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  TEMP_CLOSED: 'warning',
  DISABLED: 'neutral',
  DISCONTINUED: 'neutral',
  OUT_OF_STOCK: 'danger',

  // ---- device / activation ---------------------------------------------
  PENDING: 'warning',
  REVOKED: 'danger',

  // ---- translation workflow (translation.ts) ---------------------------
  MISSING: 'neutral',
  QUEUED: 'info',
  PROCESSING: 'busy',
  AI_TRANSLATED: 'warning',
  NEEDS_REVIEW: 'warning',
  APPROVED: 'info',
  PUBLISHED: 'success',
  OUTDATED: 'warning',

  // ---- inventory --------------------------------------------------------
  IN_STOCK: 'success',
  LOW_STOCK: 'warning',
  EXPIRED_STOCK: 'danger',

  // ---- alert severity (also used by the audit log viewer) --------------
  INFO: 'info',
  NOTICE: 'info',
  LOW: 'neutral',
  MEDIUM: 'warning',
  HIGH: 'danger',
  CRITICAL: 'danger',
  WARNING: 'warning',
  ERROR: 'danger',
  RESOLVED: 'success',
  ACKNOWLEDGED: 'info',

  // ---- provider health --------------------------------------------------
  HEALTHY: 'success',
  DEGRADED: 'warning',
  UNKNOWN: 'neutral',
  DOWN: 'danger',
};

/** Unknown statuses render neutral rather than crashing or going invisible. */
export function toneFor(status: string | null | undefined): Tone {
  if (status === null || status === undefined) return 'neutral';
  return STATUS_TONE[status.toUpperCase()] ?? 'neutral';
}

/**
 * Tailwind classes per tone.
 *
 * `soft` — badges, banners, table-cell fills (the default everywhere).
 * `solid` — one strong element: a primary button, a status dot, a progress bar.
 * `dot`   — the small circle in front of a status label.
 * `text`  — text-only usage, e.g. a number that turns red when negative.
 */
export const TONE_CLASSES: Record<Tone, { soft: string; solid: string; dot: string; text: string }> = {
  neutral: {
    soft: 'bg-neutral-bg text-neutral-fg ring-1 ring-inset ring-line',
    solid: 'bg-neutral-solid text-white',
    dot: 'bg-neutral-solid',
    text: 'text-neutral-fg',
  },
  info: {
    soft: 'bg-info-bg text-info-fg ring-1 ring-inset ring-info-solid/25',
    solid: 'bg-info-solid text-white',
    dot: 'bg-info-solid',
    text: 'text-info-fg',
  },
  success: {
    soft: 'bg-success-bg text-success-fg ring-1 ring-inset ring-success-solid/25',
    solid: 'bg-success-solid text-white',
    dot: 'bg-success-solid',
    text: 'text-success-fg',
  },
  warning: {
    soft: 'bg-warning-bg text-warning-fg ring-1 ring-inset ring-warning-solid/30',
    solid: 'bg-warning-solid text-white',
    dot: 'bg-warning-solid',
    text: 'text-warning-fg',
  },
  danger: {
    soft: 'bg-danger-bg text-danger-fg ring-1 ring-inset ring-danger-solid/25',
    solid: 'bg-danger-solid text-white',
    dot: 'bg-danger-solid',
    text: 'text-danger-fg',
  },
  busy: {
    soft: 'bg-busy-bg text-busy-fg ring-1 ring-inset ring-busy-solid/25',
    solid: 'bg-busy-solid text-white',
    dot: 'bg-busy-solid',
    text: 'text-busy-fg',
  },
  accent: {
    soft: 'bg-accent-bg text-accent-fg ring-1 ring-inset ring-accent-solid/25',
    solid: 'bg-accent-solid text-white',
    dot: 'bg-accent-solid',
    text: 'text-accent-fg',
  },
};

/**
 * Vietnamese labels for the statuses the three apps display.
 *
 * The Admin app is Vietnamese-only (see the comment in admin-app/src/App.tsx),
 * and POS/Staff are staff-facing so also Vietnamese. Only the CUSTOMER QR
 * surface is multilingual, and it gets its strings from the translation API —
 * never from this table.
 */
export const STATUS_LABEL_VI: Record<string, string> = {
  EMPTY: 'Trống',
  OCCUPIED: 'Đang phục vụ',
  RESERVED: 'Đã đặt trước',
  NEEDS_CLEANING: 'Cần dọn',
  SETTING_UP: 'Đang setup',
  LOCKED: 'Đang khóa',
  OUT_OF_SERVICE: 'Ngừng sử dụng',

  OPEN: 'Đang mở',
  BILL_REQUESTED: 'Khách xin tính tiền',
  PAYING: 'Đang thanh toán',
  CLOSED: 'Đã đóng',

  DRAFT: 'Nháp',
  PENDING_CONFIRM: 'Chờ xác nhận',
  CONFIRMED: 'Đã xác nhận',
  SENT_TO_STATION: 'Đã gửi bếp',
  IN_PRODUCTION: 'Đang chế biến',
  PARTIALLY_READY: 'Xong một phần',
  READY: 'Sẵn sàng',
  SERVED: 'Đã phục vụ',
  COMPLETED: 'Hoàn tất',
  CANCELLED: 'Đã hủy',
  REJECTED: 'Từ chối',

  UNPAID: 'Chưa thanh toán',
  PARTIALLY_PAID: 'Thanh toán một phần',
  PAID: 'Đã thanh toán',
  OVERPAID: 'Thu dư',
  REFUNDED: 'Đã hoàn tiền',
  PARTIALLY_REFUNDED: 'Hoàn một phần',
  VOIDED: 'Đã hủy bỏ',
  CAPTURED: 'Đã thu',
  AUTHORIZED: 'Đã ủy quyền',
  FAILED: 'Thất bại',

  WAITING: 'Chờ nhận',
  OFFERED: 'Đang mời nhận',
  ACCEPTED: 'Đã nhận',
  PREPARING: 'Đang làm',
  PAUSED: 'Tạm dừng',
  WAITING_INGREDIENT: 'Thiếu nguyên liệu',
  REMAKE_REQUIRED: 'Cần làm lại',
  REMAKING: 'Đang làm lại',
  WAITING_PICKUP: 'Chờ lấy món',
  PICKED_UP: 'Đã lấy món',
  DELIVERED: 'Đã giao',
  RETURNED: 'Bị trả lại',

  ASSIGNED: 'Đã giao việc',
  IN_PROGRESS: 'Đang làm',
  DONE: 'Hoàn thành',
  EXPIRED: 'Quá hạn',
  REASSIGNED: 'Đã chuyển việc',
  ESCALATED: 'Đã báo cấp trên',

  OFFLINE: 'Ngoại tuyến',
  NOT_ON_SHIFT: 'Ngoài ca',
  AVAILABLE: 'Rảnh',
  BUSY: 'Đang bận',
  ON_BREAK: 'Đang nghỉ',

  ACTIVE: 'Đang hoạt động',
  INACTIVE: 'Ngừng hoạt động',
  TEMP_CLOSED: 'Tạm đóng',
  DISABLED: 'Đã tắt',
  DISCONTINUED: 'Ngừng bán',
  OUT_OF_STOCK: 'Hết hàng',

  PENDING: 'Chờ kích hoạt',
  REVOKED: 'Đã thu hồi',

  MISSING: 'Chưa có',
  QUEUED: 'Trong hàng đợi',
  PROCESSING: 'Đang xử lý',
  AI_TRANSLATED: 'AI đã dịch',
  NEEDS_REVIEW: 'Cần soát',
  APPROVED: 'Đã duyệt',
  PUBLISHED: 'Đã công khai',
  OUTDATED: 'Đã lỗi thời',

  IN_STOCK: 'Còn hàng',
  LOW_STOCK: 'Sắp hết',
  EXPIRED_STOCK: 'Hết hạn',

  INFO: 'Thông tin',
  NOTICE: 'Ghi nhận',
  LOW: 'Thấp',
  MEDIUM: 'Trung bình',
  HIGH: 'Cao',
  CRITICAL: 'Nghiêm trọng',
  WARNING: 'Cảnh báo',
  ERROR: 'Lỗi',
  RESOLVED: 'Đã xử lý',
  ACKNOWLEDGED: 'Đã tiếp nhận',

  HEALTHY: 'Bình thường',
  DEGRADED: 'Suy giảm',
  UNKNOWN: 'Không rõ',
  DOWN: 'Mất kết nối',
};

/** Falls back to the raw code so an unmapped status is visible, not blank. */
export function statusLabelVi(status: string | null | undefined): string {
  if (status === null || status === undefined || status === '') return '—';
  return STATUS_LABEL_VI[status.toUpperCase()] ?? status;
}
